import { logError, logInfo } from '../shared/utils/logging';
import { REGISTERED_APPS } from '../shared/types/Apps';
import { ButtonAction, Product } from '../types';
import { getDiscountCodesStringSeparatedByCommaAndEscaped } from '../shared/utils/discounts';
import captureException from './sentry';
import { getCurrentLocalePrefix } from '../shared/api/utils';

/*
 * Extracted functions from the CandyRackApp.
 * Some could potentially be moved after careful consideration to generic files (not limited to Add To Cart)
 */

export function validateForm(button: HTMLElement) {
  if (window.CANDYRACK_DISABLE_FORM_VALIDATION) {
    return true;
  }

  try {
    if (window.CANDYRACK_CAN_ATC && !window.CANDYRACK_CAN_ATC(button)) {
      logInfo(
        'CANDYRACK_CAN_ATC prevented ATC action',
        REGISTERED_APPS.ADD_TO_CART,
      );
      return;
    }
  } catch (error) {
    logError('CANDYRACK_CAN_ATC failed', REGISTERED_APPS.ADD_TO_CART, error);
  }

  try {
    const form = button.closest('form');
    if (!form || !(form instanceof HTMLFormElement)) {
      return true;
    }
    // Uploadkit app form validation
    if (form.classList.contains('uploadkit-invalid')) {
      return false;
    }
    const isValidForm = form.checkValidity();
    logInfo(`form.isValid: ${isValidForm}`, REGISTERED_APPS.ADD_TO_CART);
    return isValidForm;
  } catch (error) {
    logError(error, REGISTERED_APPS.ADD_TO_CART);
    return true;
  }
}

export function clickHiddenButton(button: HTMLElement) {
  window.CandyRack.buttonState = 'pass-through';
  if (window.$ && window.hulkapps && window.hulkapps.is_product_option) {
    logInfo(
      'Hulkapps Product Options found. Clicking using jQuery.',
      REGISTERED_APPS.ADD_TO_CART,
    );
    try {
      window.$(button).trigger('click');
    } catch (error) {
      logError(
        'jQuery click failed. Falling back to .click()',
        REGISTERED_APPS.ADD_TO_CART,
        error,
      );
      button.click();
    }
  } else if (window.Gs && window.gsJquery) {
    logInfo(
      'Giftship found. Clicking using jQuery.',
      REGISTERED_APPS.ADD_TO_CART,
    );
    try {
      window.gsJquery(button).trigger('click');
    } catch (error) {
      logError(
        'jQuery click failed. Falling back to .click()',
        REGISTERED_APPS.ADD_TO_CART,
        error,
      );
      button.click();
    }
  } else {
    button.click();
  }
}

export function stayOnProductPage(button: HTMLElement) {
  window?.CandyRackSliderCart?.openCart();
  const form = button.closest('form');
  if (form) {
    form.insertAdjacentHTML(
      'beforeend',
      `<input type="hidden" name="return_to" value="${window.location.pathname}" />`,
    );
  }
}

export function redirect(action: ButtonAction) {
  const localePrefix = getCurrentLocalePrefix(window.location.pathname);

  if (action === 'redirect_to_cart') {
    window.location.href = `${localePrefix}/cart`;
  }

  if (action === 'redirect_to_checkout') {
    const discountCode = getDiscountCodesStringSeparatedByCommaAndEscaped(
      REGISTERED_APPS.ADD_TO_CART,
    );
    const discountParams = discountCode ? `?discount=${discountCode}` : '';
    window.location.href = `${localePrefix}/checkout${discountParams}`;
  }
}

function findFormAndQuantityForButton(
  button: HTMLElement,
  formSelector: string,
) {
  const productForm = button.closest(formSelector);
  if (productForm) {
    const quantityElements =
      productForm.querySelectorAll<HTMLInputElement>('[name=quantity]');
    let [quantityElement]: Array<HTMLInputElement | null> =
      Array.from(quantityElements);
    if (quantityElements.length > 1) {
      quantityElement = productForm.querySelector('[name=quantity]:checked');
    }
    if (quantityElement) {
      return quantityElement.value;
    }
  }
  return null;
}

/**
 * Retrieves the selected quantity associated with a button element when a custom quantity function is defined.
 * If no custom quantity function is defined, most nearby form element is searched for the quantity element.
 * If no form element exists, the value 1 is returned.
 * Note that we can't just search inside the whole document instead, as we wouldn't check cases such as for example
 * two sets of HTML inputs on the page and some of them are hidden / shown depending on mobile/desktop settings.
 * Also, Candy Rack appears on Collection pages, where for example multiple Add To Cart buttons with multiple
 * Quantity selectors exist, and we can't just take the first occurence there.
 *
 * Further improvements will follow in APPS-6798:
 * 1) We will additionaly check the document itself as a fallback scenario.
 * 2) We will investigate popular themes to catch corner cases.
 *
 * @param {HTMLElement} button - The button element for which to determine the quantity if
 * the CANDYRACK_CUSTOM_QUANTITY_FUNCTION exists.
 * @returns {number} The selected quantity or 1 as a default value if no quantity elements are found.
 */
export function quantityForButton(button: HTMLElement) {
  logInfo(
    `Getting quantity for button ${button?.className}`,
    REGISTERED_APPS.ADD_TO_CART,
  );
  if (
    window.CANDYRACK_CUSTOM_QUANTITY_FUNCTION &&
    typeof window.CANDYRACK_CUSTOM_QUANTITY_FUNCTION === 'function'
  ) {
    const quantity = window.CANDYRACK_CUSTOM_QUANTITY_FUNCTION(button);
    logInfo(
      `Returning quantity calculated via custom function: ${quantity}`,
      REGISTERED_APPS.ADD_TO_CART,
    );
    return quantity;
  }

  const selectors = [
    'form', // Generic
    'product-info', // Dawn
  ];

  for (const formSelector of selectors) {
    const quantity = Number(findFormAndQuantityForButton(button, formSelector));
    // We need to explicitly check for 0 because:
    // Number('') -> 0
    // (0 || 1) -> 1
    if (quantity !== 0 && Number.isFinite(quantity)) {
      logInfo(`Calculated quantity: ${quantity}`, REGISTERED_APPS.ADD_TO_CART);
      return quantity;
    }
  }
  logInfo(
    `No selector found, defaulting to a quantity of 1.`,
    REGISTERED_APPS.ADD_TO_CART,
  );
  return 1;
}

export async function variantIdForButton(button: HTMLElement) {
  try {
    if (window.CANDYRACK_VARIANT_SELECTOR_FUNCTION) {
      return await window.CANDYRACK_VARIANT_SELECTOR_FUNCTION(button);
    }
  } catch (error) {
    logError(
      'CANDYRACK_VARIANT_SELECTOR_FUNCTION failed',
      REGISTERED_APPS.ADD_TO_CART,
      error,
    );
  }

  const closestForm = button.closest('form');
  if (!closestForm) {
    return;
  }
  const idElement = closestForm.querySelector<HTMLInputElement>(
    '[name="id"],[name="id[]"]',
  );
  if (!idElement) {
    return;
  }
  const variantId = idElement.value;

  if (!isNaN(Number(variantId))) {
    return Number(variantId);
  }
}

export async function productIsMissingTheVariant(
  product: Product,
  button: HTMLElement,
) {
  if (product) {
    // Make sure to allow ReCharge generated products
    const rechargeSubscriptionId = document.querySelectorAll(
      '[name="properties[subscription_id]"], [name="properties[shipping_interval_unit_type]"], [name="properties[shipping_interval_frequency]"]',
    );
    if (rechargeSubscriptionId.length > 0) {
      logInfo(
        'rechargeSubscriptionId found, overriding check.',
        REGISTERED_APPS.ADD_TO_CART,
      );
      return;
    }

    try {
      const variantId = await variantIdForButton(button);
      if (variantId) {
        const variant = product.variants.find(
          (v) => v.id === Number(variantId),
        );
        if (!variant) {
          logInfo(
            `Variant ${variantId} not found in ${product.handle}`,
            REGISTERED_APPS.ADD_TO_CART,
          );
          return true;
        }
      }
    } catch (error) {
      logError('Variant check failed', REGISTERED_APPS.ADD_TO_CART, error);
      captureException(error);
    }
  }
}

export function getCustomProperties(button: HTMLElement) {
  let properties: Record<string, string> = {};
  const form = button.closest('form');
  if (!form) return properties;

  const propertiesElements = Array.from(
    form.querySelectorAll<HTMLInputElement>('[name^="properties"]'),
  );

  for (const propertiesElement of propertiesElements) {
    //@ts-ignore
    let [propertyName] = propertiesElement.name.match(/\[(.*)\]/) || [];
    if (propertyName && propertiesElement.value) {
      // Removing [] from the property
      // eslint-disable-next-line
      propertyName = propertyName.replace(/[[\]]/g, '');
      properties[propertyName] = propertiesElement.value;
    }
  }
  if (window.CANDYRACK_CUSTOM_PROPERTIES) {
    properties = { ...properties, ...window.CANDYRACK_CUSTOM_PROPERTIES };
  }
  return properties;
}

export function getSelectedVariantIdFromURL() {
  const variantIdMatch = window.location.search.match(
    /.*variant=([^&|\n|\t\s]+)/,
  );
  if (variantIdMatch) {
    return Number(variantIdMatch[1]);
  }
}

export function getDefaultProductVariantId(product?: Product) {
  return product?.variants[0]?.id;
}
